import {useContext, useEffect, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Editor, JSONContent} from '@tiptap/react';
import {AxiosError} from 'axios';
import {
  Message,
  MessageAttachment,
  MessageThread,
  MessageThreadPatch,
  MessageThreadScope,
  MessageThreadSummaries,
  MessageThreadSummary,
} from '@/api/dto/message';
import {ClientProfile} from '@/api/dto/client-profile';
import {CareJourneyDetailsResponse} from '@/api/dto/care-journey';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAuthStore} from '@/store/auth_store';
import {useActiveRoster} from '@/store/roster_store';
import {useAlertStore} from '@/store/alert_store';
import {useWebSocketSubscription} from '@/hooks/useWebSocketSubscription';
import {useWebSocketError} from '@/hooks/useWebSocketError';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import {ICONS} from 'patient-ping-remedy/packages/icon';
import {StandardTypography} from 'patient-ping-remedy/packages/typography';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Banner, {BannerTypes} from 'patient-ping-remedy/packages/banner';
import {
  Menu,
  MenuItem,
  MenuItemSpacer,
  MenuItemTrailingIcon,
  MenuItemTypography
} from 'patient-ping-remedy/packages/menu';
import IconButton from 'patient-ping-remedy/packages/icon-button';
import ToolTip from 'patient-ping-remedy/packages/tool-tip';
import Popover from 'patient-ping-remedy/packages/popover';
import SlideInPane from 'patient-ping-remedy/packages/slide-in-pane';
import Chat from '@common/messages/Chat';
import InfoIconTooltip from '@common/InfoIconTooltip';
import DeleteModal from '@common/DeleteModal';
import MessageThreadMuteModal from '@clients/profile_page/messages_tab/MessageThreadMuteModal';
import MessageThreadTitle from '@/components/pages/messages/MessageThreadTitle';
import MessageThreadDetails from '@/components/pages/messages/details/MessageThreadDetails';
import {
  Content,
  Details,
  Subject,
  SubjectContent,
  SubjectLabel
} from '@clients/profile_page/messages_tab/MessageThreadDrawer.styles';
import {MixpanelEventType, MixpanelTaskEvent} from '@/api/dto/mixpanel';
import {useMixpanel} from '@/hooks/useMixpanel';
import {useLastSeenMessage} from '@/hooks/useLastSeenMessage';
import PermissionsHelpers from '@/helpers/permissions_helpers';
import DisplayHelpers from '@/helpers/display_helpers';
import Helpers from '@/helpers/helpers';
import {Feature, FeatureNew, FeaturePermissionLevel} from "@/api/dto/permissions";
import {FeatureToggle} from "@/api/dto/dto";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";
import {useStateConfig} from '@/hooks/useStateConfig';

type AttachmentDelete = {
  message: Message;
  attachment: MessageAttachment;
}

type Props = {
  patient: ClientProfile;
  messageThreadSummary?: MessageThreadSummary;
  careJourney?: CareJourneyDetailsResponse;
  referralId?: string;
  unitId?: string;
  scope?: MessageThreadScope;
  simple?: boolean;
  isOpen?: boolean;
  onClose?(): void;
  onNewMessageThread(newMessageThread: MessageThreadSummary): void;
}

function findNodes(content: JSONContent, type: string) {
  const nodes: JSONContent[] = [];
  collectNodes(content, nodes, type);

  return nodes;
}

function collectNodes(content: JSONContent, nodes: JSONContent[], type: string) {
  for (const node of content.content ?? []) {
    if (node.type === type) {
      nodes.push(node);
    }

    collectNodes(node, nodes, type);
  }
}

export default function MessageThreadDrawer({
  patient,
  messageThreadSummary,
  careJourney,
  referralId,
  unitId,
  scope = MessageThreadScope.INTERNAL,
  simple,
  isOpen = true,
  onClose = () => {},
  onNewMessageThread,
}: Props) {
  const { carecoApi, webSocketApi } = useContext(CarecoApiContext);
  const { currentUser } = useAuthStore();
  const currentRoster = useActiveRoster();
  const { addAlert } = useAlertStore();
  const queryClient = useQueryClient();
  const {mixpanelEventProperties, getCurrentGroupDetails} = useMixpanel();
  const {checkBooleanToggle} = useFeatureToggles();
  const stateConfig = useStateConfig();

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);
  const dischargeAttachmentsOnly = !simple && !!stateConfig?.features?.dischargeAttachmentsOnly;

  const [tempMessageThread, setTempMessageThread] = useState<MessageThread>();
  const [attachmentDelete, setAttachmentDelete] = useState<AttachmentDelete | null>(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isRendered, setIsRendered] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isSubjectEdit, setIsSubjectEdit] = useState(false);
  const [isMuteEdit, setIsMuteEdit] = useState(false);

  const [subject, setSubject] = useState(messageThreadSummary?.subject ?? '');
  const subjectRef = useRef(messageThreadSummary?.subject ?? '');

  const isOpenRef = useRef(isOpen);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const messageThreadSummaryRef = useRef<MessageThreadSummary | null>(null);

  const canViewTasks = isNewPermissionsEnabled ?
    PermissionsHelpers.canForUserFeaturePermissionRosterNew(
      currentUser!,
      currentRoster!,
      FeaturePermissionLevel.VIEW,
      FeatureNew.TASKS,
    ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
      currentUser!,
      currentRoster!,
      FeaturePermissionLevel.VIEW,
      Feature.TASKS,
    );

  useWebSocketSubscription(
    () => {
      if (currentUser && isOpen && !messageThreadSummary) {
        return webSocketApi?.subscribeChatThreadUser(currentUser.id, (newMessageThread) => {
          onNewMessageThread(newMessageThread);
        });
      }
    },
    [currentUser],
    [isOpen, messageThreadSummary],
  );

  useWebSocketSubscription(
    () => {
      if (patient && !careJourney && !referralId && !unitId) {
        return webSocketApi?.subscribeChatThreadClient(patient.patient.patientId, (updateMessageThread) => {
          handleUpdateMessageThread(updateMessageThread);
          handleReadMessage(updateMessageThread);
        });
      }
    },
    [patient, careJourney, referralId, unitId],
  );

  useWebSocketSubscription(
    () => {
      if (patient && canViewTasks && !referralId && !unitId) {
        return webSocketApi?.subscribeChatThreadClientCareJourney(
          patient.patient.patientId,
          careJourney?.careJourneyInstanceId ?? null,
          (updateMessageThread) => {
            handleUpdateMessageThread(updateMessageThread);
            handleReadMessage(updateMessageThread);
          });
      }
    },
    [patient, careJourney, referralId, unitId],
    [canViewTasks],
  );

  useWebSocketSubscription(
    () => {
      if (patient && !careJourney) {
        return webSocketApi?.subscribeChatThreadClientReferral(
          patient.patient.patientId,
          referralId ?? null,
          unitId ?? null,
          (updateMessageThread) => {
            handleUpdateMessageThread(updateMessageThread);
            handleReadMessage(updateMessageThread);
          });
      }
    },
    [patient, careJourney, referralId, unitId],
  );

  useWebSocketError((message) => {
    if (isOpen) {
      if (!messageThreadSummaryRef.current) {
        setIsDisabled(false);
        setTempMessageThread(undefined);
      } else {
        queryClient.setQueryData<MessageThread>(
          ['messageThreads', messageThreadSummaryRef.current.id],
          (messageThread) => {
            if (messageThread) {
              return {
                ...messageThread,
                messages: messageThread.messages.filter((message) => message.id > 0),
              };
            }
          });
      }

      Helpers.alertOnError(`${message} ${GENERICERROR}`, message);
    }
  }, [isOpen]);

  const { data: messageThread, isLoading, isFetching, isFetched } = useQuery({
    queryKey: ['messageThreads', messageThreadSummary?.id],
    queryFn: () => carecoApi?.getMessageThread(messageThreadSummary!.id),
    enabled: !!carecoApi && !!messageThreadSummary,
    staleTime: careJourney ? 0 : undefined,
    refetchOnWindowFocus: false,
  });

  const { mutate: handleMessageThreadPatch } = useMutation({
    mutationFn: async (messageThreadPatch: MessageThreadPatch) =>
      carecoApi?.patchMessageThread(messageThreadSummary!.id, messageThreadPatch),
    onSuccess(_, messageThreadPatch) {
      updateMessageThreadSummary({ ...messageThreadSummary!, ...messageThreadPatch });
      setIsSubjectEdit(false);
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to update message thread subject. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const { mutate: handleAttachmentDeleteConfirm } = useMutation({
    mutationFn: async (attachmentDelete: AttachmentDelete) =>
      carecoApi?.deleteMessageAttachment(attachmentDelete.attachment.id),
    onSuccess(_, attachmentDelete) {
      addAlert({
        type: ToastType.SUCCESS,
        content: 'Message attachment deleted successfully.',
      });
      commitAttachmentDelete(attachmentDelete);
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(`Failed to delete message attachment. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const { scrollParent, lastSeenMessageId, handleTopmostMessage } = useLastSeenMessage({
    messageThreadSummary: messageThreadSummaryRef,
    messageThread,
    isOpen,
    isReady: isRendered && !isFetching && isFetched,
    content: contentRef,
  });

  useEffect(() => {
    if (!isOpen && messageThreadSummaryRef.current && lastSeenMessageId.current) {
      const message = messageThread?.messages.find((message) => message.id === lastSeenMessageId.current);
      if (message) {
        updateMessageThreadSummary({
          ...messageThreadSummaryRef.current,
          lastSeenMessage: message,
        });
      }
    }

    setTempMessageThread(undefined);
    setIsEmpty(true);
    setIsRendered(false);
    setIsPopoverOpen(false);
    setIsSubjectEdit(false);
    setSubject(messageThreadSummary?.subject ?? '');

    isOpenRef.current = isOpen;
    contentRef.current = null;
    lastSeenMessageId.current = null;
  }, [isOpen, messageThreadSummary?.id]);

  useEffect(() => {
    subjectRef.current = subject;
  }, [subject]);

  useEffect(() => {
    if (messageThreadSummary?.id) {
      setIsDisabled(false);
    }

    messageThreadSummaryRef.current = messageThreadSummary ?? null;
  }, [messageThreadSummary]);

  const attachContentRef = (element: HTMLDivElement | null) => {
    if (isOpenRef.current && element) {
      setIsRendered(true);
      contentRef.current = element;
    }
  };

  const hidePopover = (callback?: () => void) => () => {
    setIsPopoverOpen(false);
    callback?.();
  };

  const handleUpdate = (editor: Editor) => {
    setIsEmpty(editor.isEmpty);
  };

  const handleNewMessage = async (content: JSONContent, attachments: File[]) => {
    if (currentUser) {
      if (!messageThreadSummaryRef.current) {
        setIsDisabled(true);
        setTempMessageThread({
          id: -1,
          clientId: patient.patient.patientId,
          careJourneyInstanceId: careJourney?.careJourneyInstanceId,
          scope,
          subject: subjectRef.current,
          messages: [
            {
              id: -1,
              user: currentUser,
              content,
              attachments: attachments.map((attachment, index) => ({
                id: -index - 1,
                messageId: -1,
                s3Key: attachment.name,
              })),
              createdAt: new Date().toISOString(),
            },
          ],
        });

        if (careJourney?.careJourneyInstanceId) {
          carecoApi?.postMixpanelEvent({
            eventName: MixpanelEventType.BIH_MESSAGE_THREAD_STARTED_IN_TASK,
            ...mixpanelEventProperties!
          });
        }
      } else {
        queryClient.setQueryData<MessageThread>(
          ['messageThreads', messageThreadSummaryRef.current.id],
          (messageThread) => {
            if (messageThread) {
              const messageId = -messageThread.messages.filter((message) => message.id < 0).length - 1;
              return {
                ...messageThread,
                messages: [
                  ...messageThread.messages,
                  {
                    id: messageId,
                    user: currentUser,
                    content,
                    attachments: attachments.map((attachment, index) => ({
                      id: -index - 1,
                      messageId,
                      s3Key: attachment.name,
                    })),
                    createdAt: new Date().toISOString(),
                  },
                ],
              };
            }
          });
      }

      carecoApi?.postMixpanelEvent({
        eventName: MixpanelEventType.BIH_SENT_IN_APP_MESSAGE,
        ...mixpanelEventProperties!
      });

      if (careJourney?.careJourneyInstanceId && currentRoster) {
        const taskMessageEvent: MixpanelTaskEvent = {
          eventName: MixpanelEventType.BIH_TASK_MESSAGE_SENT,
          eventType: careJourney.eventType,
          careJourneyName: careJourney.journeyType,
          ...mixpanelEventProperties!,
          ...getCurrentGroupDetails(currentRoster)
        };
        carecoApi?.postMixpanelEvent(taskMessageEvent);
      }

      // Track discharge instructions upload separately
      if (dischargeAttachmentsOnly && attachments.length > 0) {
        carecoApi?.postMixpanelEvent({
          eventName: MixpanelEventType.BIH_DISCHARGE_INSTRUCTIONS_UPLOADED_IN_MESSAGES,
          ...mixpanelEventProperties!
        });
      }
    }

    scrollParent.current?.scrollTo({ top: 0 });

    let messageAttachmentIds: number[] = [];

    if (attachments.length) {
      const messageAttachments = await carecoApi?.createMessageAttachments(patient.patient.patientId, attachments);
      messageAttachmentIds = messageAttachments?.map((messageAttachment) => messageAttachment.id) ?? [];
    }

    webSocketApi?.publishChatMessage(
      messageThreadSummaryRef.current?.id ?? null,
      patient.patient.patientId,
      messageThreadSummaryRef.current?.careJourneyInstanceId ?? careJourney?.careJourneyInstanceId ?? null,
      messageThreadSummaryRef.current?.referralId ?? referralId ?? null,
      messageThreadSummaryRef.current?.unitId ?? unitId ?? null,
      messageAttachmentIds,
      scope,
      subjectRef.current,
      content,
    );
  };

  const handleUpdateMessageThread = (updateMessageThread: MessageThreadSummary) => {
    updateMessageThreadSummary(updateMessageThread);

    queryClient.setQueryData<MessageThread>(
      ['messageThreads', updateMessageThread.id],
      (messageThread) => {
        if (messageThread) {
          const optimisticUpdateMessage = messageThread.messages
            .filter((message) => message.id < 0)
            .reduce<Message | null>((prev, message) =>
              prev && prev.id > message.id ? prev : message, null);

          const optimisticUpdateIndex = messageThread.messages.findIndex((message) =>
            message.id === optimisticUpdateMessage?.id);

          if (optimisticUpdateIndex !== -1 && optimisticUpdateMessage) {
            return {
              ...messageThread,
              subject: updateMessageThread.subject,
              messages: [
                ...messageThread.messages.slice(0, optimisticUpdateIndex),
                {
                  ...updateMessageThread.latestMessage,
                  createdAt: optimisticUpdateMessage.createdAt,
                },
                ...messageThread.messages.slice(optimisticUpdateIndex + 1),
              ],
            };
          }

          const index = messageThread.messages.findIndex((message) =>
            message.id === updateMessageThread.latestMessage.id);

          if (index === -1) {
            return {
              ...messageThread,
              subject: updateMessageThread.subject,
              messages: [...messageThread.messages, updateMessageThread.latestMessage],
            };
          } else {
            return {
              ...messageThread,
              subject: updateMessageThread.subject,
              messages: [
                ...messageThread.messages.slice(0, index),
                {
                  ...messageThread.messages[index],
                  ...updateMessageThread.latestMessage,
                },
                ...messageThread.messages.slice(index + 1),
              ],
            };
          }
        }
      });
  };

  const handleReadMessage = (updateMessageThread: MessageThreadSummary) => {
    if (isOpenRef.current && messageThreadSummaryRef.current) {
      // This timeout makes sure messageThreadSummaryRef is up-to-date
      setTimeout(() => {
        handleTopmostMessage().then(() => {
          updateMessageThreadSummary({
            ...messageThreadSummaryRef.current!,
            lastSeenMessage: updateMessageThread.latestMessage,
          });
        });
      });
    }
  };

  const updateMessageThreadSummary = (updateMessageThread: MessageThreadSummary) => {
    queryClient.setQueryData<MessageThreadSummaries>(
      ['messageThreadSummaries', patient?.patient.patientId, ...(careJourney ? [careJourney.careJourneyInstanceId] : [])],
      (messageThreadSummaries) => {
        const index = messageThreadSummaries?.messageThreadSummaries.findIndex((messageThreadSummary) =>
          messageThreadSummary.id === updateMessageThread.id) ?? -1;

        // This logic should only be used on message threads summaries with are fetched with participant_only=true flag,
        // otherwise different places might show threads regardless if user is participant or not
        if (index === -1 && !careJourney && !referralId && !unitId) {
          const usersIds = updateMessageThread.users.map((user) => user.id);
          const groupsIds: number[] = [];

          for (const node of findNodes(updateMessageThread.latestMessage.content, 'mention')) {
            if (node.attrs?.category === 'user') {
              usersIds.push(node.attrs.id);
            } else if (node.attrs?.category === 'group') {
              groupsIds.push(node.attrs.id);
            }
          }

          const currentUserId = currentUser?.id ?? '';
          const currentUserRosterIds = currentUser?.rosters.map((roster) => roster.roster.id) ?? [];

          if (
            !usersIds.includes(currentUserId) &&
            !groupsIds.some((groupId) => currentUserRosterIds?.includes(groupId))
          ) {
            return;
          }
        }

        let updatedMessageThreadSummaries;

        if (index === -1 || !messageThreadSummaries) {
          updatedMessageThreadSummaries = [
            ...(messageThreadSummaries?.messageThreadSummaries ?? []),
            updateMessageThread,
          ];
        } else {
          const isLatest = new Date(messageThreadSummaries.messageThreadSummaries[index].latestMessage.createdAt)
            < new Date(updateMessageThread.latestMessage.createdAt);

          updatedMessageThreadSummaries = [
            ...messageThreadSummaries.messageThreadSummaries.slice(0, index),
            {
              ...messageThreadSummaries.messageThreadSummaries[index],
              subject: updateMessageThread.subject,
              muted: updateMessageThread.muted
                ?? messageThreadSummaries.messageThreadSummaries[index].muted,
              latestMessage: isLatest
                ? updateMessageThread.latestMessage
                : messageThreadSummaries.messageThreadSummaries[index].latestMessage,
              lastSeenMessage: updateMessageThread.lastSeenMessage
                ?? messageThreadSummaries.messageThreadSummaries[index].lastSeenMessage,
              users: Helpers.removeDuplicates([
                ...messageThreadSummaries.messageThreadSummaries[index].users,
                ...updateMessageThread.users,
              ], 'id'),
              attachments: Helpers.removeDuplicates([
                ...messageThreadSummaries.messageThreadSummaries[index].attachments
                  .filter((oldAttachment) =>
                    oldAttachment.messageId !== updateMessageThread.latestMessage.id || updateMessageThread.attachments
                      .map((newAttachment => newAttachment.id)).includes(oldAttachment.id)),
                ...updateMessageThread.attachments
                  .filter((newAttachment) => !messageThreadSummaries.messageThreadSummaries[index].attachments
                    .map((oldAttachment) => oldAttachment.id).includes(newAttachment.id))
              ], 'id'),
            },
            ...messageThreadSummaries.messageThreadSummaries.slice(index + 1),
          ];
        }

        return {
          rosterTotal: updatedMessageThreadSummaries.length,
          // This cache holds the full, unpaged set of summaries for a single client.
          hasMore: messageThreadSummaries?.hasMore ?? false,
          messageThreadSummaries: updatedMessageThreadSummaries,
        };
      });
  };

  const handleAttachmentDelete = (message: Message, attachment: MessageAttachment) => {
    setAttachmentDelete({ message, attachment });
  };

  const commitAttachmentDelete = (attachmentDelete: AttachmentDelete) => {
    queryClient.setQueryData<MessageThread>(
      ['messageThreads', messageThreadSummaryRef.current?.id],
      (messageThread) => {
        if (messageThread) {
          const index = messageThread.messages.findIndex((message) =>
            message.id === attachmentDelete.message.id);

          if (index === -1) {
            return messageThread;
          }

          return {
            ...messageThread,
            messages: [
              ...messageThread.messages.slice(0, index),
              {
                ...messageThread.messages[index],
                attachments: messageThread.messages[index].attachments
                  .filter((attachment) => attachment.id !== attachmentDelete.attachment.id),
              },
              ...messageThread.messages.slice(index + 1),
            ],
          };
        }
      });
  };

  const chatContent = (
    <>
      <Content ref={attachContentRef} isScrollable={!!careJourney}>
        <StandardTypography>
          <MessageThreadDetails messageThreadSummary={messageThreadSummary} interactive />
        </StandardTypography>
        {!simple && !messageThreadSummary && (
          <>
            {!isEmpty && !subject && (
              <Banner type={BannerTypes.WARNING}>
                The message thread will not have a subject.
                Adding subject helps people understand the context.
              </Banner>
            )}
            <Subject
              labelText={(
                <SubjectLabel>
                  Subject name
                  <InfoIconTooltip
                    position="bottom"
                    text="Provide informative subject to give more context to messages. The subject will be visible to all."
                  />
                </SubjectLabel>
              ) as unknown as string}
              placeholder="Add a subject"
              onChange={(e) => setSubject(e.target.value)}
            />
          </>
        )}
        <Chat
          thread={messageThread ?? tempMessageThread}
          lastSeenMessage={messageThreadSummary?.lastSeenMessage}
          isLoading={isLoading}
          isDisabled={isDisabled || isLoading}
          dischargeAttachmentsOnly={dischargeAttachmentsOnly}
          onUpdate={handleUpdate}
          onMessage={handleNewMessage}
          onAttachmentDelete={handleAttachmentDelete}
        />
      </Content>
      <DeleteModal
        title="Attachment"
        isOpen={!!attachmentDelete}
        close={() => setAttachmentDelete(null)}
        confirm={async () => handleAttachmentDeleteConfirm(attachmentDelete!)}
      />
      {messageThreadSummary && (
        <MessageThreadMuteModal
          patient={patient}
          messageThreadSummary={messageThreadSummary}
          isOpen={isMuteEdit}
          close={() => setIsMuteEdit(false)}
          confirm={() => handleMessageThreadPatch({ muted: !messageThreadSummary.muted })}
        />
      )}
    </>
  );

  const subjectContent = (
    <SubjectContent>
      <Subject
        labelText="Subject name"
        placeholder="Add a subject"
        defaultValue={messageThreadSummary?.subject ?? ''}
        onChange={(e) => setSubject(e.target.value)}
      />
      <Button
        styleType={StyleType.TERTIARY}
        onClick={() => setIsSubjectEdit(false)}
      >
        Cancel
      </Button>
      <Button
        styleType={StyleType.SECONDARY}
        disabled={messageThreadSummary?.subject === subject}
        onClick={() => handleMessageThreadPatch({ subject })}
      >
        Save
      </Button>
    </SubjectContent>
  );

  const content = isSubjectEdit
    ? subjectContent
    : chatContent;

  if (simple) {
    return content;
  }

  return (
    <SlideInPane
      onClose={onClose}
      isOpen={isOpen}
      showNav={false}
      headerText={(
        <MessageThreadTitle
          messageThread={messageThreadSummary ?? tempMessageThread}
          about={DisplayHelpers.formatName(patient.patient.firstName, patient.patient.middleName, patient.patient.lastName)}
          simple
          data-testid="message-thread-drawer-title"
        />
      ) as unknown as string}
      headerChildren={messageThreadSummary && !isSubjectEdit && (
        <Details>
          <Popover
            toggle={() => setIsPopoverOpen(!isPopoverOpen)}
            isOpen={isPopoverOpen}
            position="bottom-end"
            content={
              <Menu onHide={hidePopover()} width="25rem" marginTop marginBottom>
                <MenuItem onClick={hidePopover(() => setIsSubjectEdit(true))}>
                  <MenuItemTypography>Edit subject</MenuItemTypography>
                </MenuItem>
                <MenuItem onClick={hidePopover(() => setIsMuteEdit(true))}>
                  <MenuItemTypography>
                    {messageThreadSummary.muted ? 'Unmute' : 'Mute'}
                  </MenuItemTypography>
                  <MenuItemSpacer />
                  <ToolTip
                    type="dark"
                    position="bottom"
                    text={messageThreadSummary.muted
                      ? 'You will be notified of new messages in inbox.'
                      : 'You no longer will be notified of new messages in inbox, but you can still reply in thread.'}
                  >
                    <MenuItemTrailingIcon iconClass={ICONS['info-circle']} />
                  </ToolTip>
                </MenuItem>
              </Menu>
            }
          >
            <IconButton iconClass={ICONS['ellipsis-v']} />
          </Popover>
        </Details>
      )}
      width="60rem"
    >
      {content}
    </SlideInPane>
  );
}
